import { jsonDateParser } from 'json-date-parser'

import { AirspaceType, isKnownAirspaceType } from '../protocols/Airspaces'
import { UserChartInfo } from '../protocols/UserChart'
import { UserGeometryInfo } from '../protocols/UserGeometry'
import { SerializableState } from './SerializableState'

export type BaseChartType = 'WAC' | 'ENRCH' | 'ENRCL'
export type OverlayChartType = 'REA' | 'ARC'
export type Overlay2ChartType = 'REH'
export type Overlay3ChartType = 'REUL'
export type BaseMapType = 'ROAD' | 'ROAD-DARK' | 'TERRAIN' | 'SAT' | 'CL-SAT'

export interface MapLayerStateProps {
  // Configs
  nightMode: boolean

  // Map
  baseMap: BaseMapType

  // Charts
  baseChart: BaseChartType
  overlayChart: OverlayChartType
  overlay2Chart: Overlay2ChartType
  overlay3Chart: Overlay3ChartType

  // Airspace
  adiz: boolean
  afis: boolean
  fir: boolean
  firSector: boolean
  tma: boolean
  tmaSector: boolean
  cta: boolean
  ctaSector: boolean
  ctr: boolean
  freeRouteAreas: boolean
  fis: boolean
  atz: boolean
  fiz: boolean
  fca: boolean
  fcaReh: boolean
  fcaAd: boolean
  suaDangerous: boolean
  suaProhibited: boolean
  suaRestricted: boolean
  // Toggles de tipos de airspace não catalogados (chaves cruas do dataset).
  airspaceExtras: Record<string, boolean>

  // ADHP
  aerodromes: boolean
  helipads: boolean
  pavedRunway: boolean
  nightOperation: boolean
  ats: boolean
  ifr: boolean
  minRunwayLength: number
  userWaypoints: boolean
  obstacles: boolean

  // User Charts
  userCharts: UserChartInfo[]

  // User Geometries
  userGeometries: UserGeometryInfo[]
  navaids: boolean
}

const MAX_ACTIVE_USER_CHARTS = 100
const MAX_ACTIVE_USER_GEOMETRIES = 100

const INITIAL_STATE: MapLayerStateProps = {
  nightMode: !(new Date().getHours() > 6 && new Date().getHours() < 18),

  // Map
  baseMap: 'ROAD',

  // Charts
  baseChart: 'WAC',
  overlayChart: null,
  overlay2Chart: null,
  overlay3Chart: null,

  // Airspace
  adiz: false,
  afis: false,
  fir: false,
  firSector: false,
  tma: false,
  tmaSector: false,
  cta: false,
  ctaSector: false,
  ctr: false,
  freeRouteAreas: false,
  fis: false,
  atz: false,
  fiz: false,
  fca: false,
  fcaReh: false,
  fcaAd: false,
  suaDangerous: false,
  suaProhibited: false,
  suaRestricted: false,
  airspaceExtras: {},

  // ADHP
  aerodromes: true,
  helipads: false,
  pavedRunway: false,
  nightOperation: false,
  ats: false,
  ifr: false,
  minRunwayLength: 0,
  userWaypoints: false,
  obstacles: false,

  // User Charts
  userCharts: [],

  // User Geometries
  userGeometries: [],
  navaids: false
}

export class MapLayerState extends SerializableState<MapLayerStateProps> {
  constructor() {
    super(INITIAL_STATE)
  }

  fromString(state: string): void {
    const parsed = JSON.parse(state, jsonDateParser) as Partial<MapLayerStateProps>
    // Blobs persistidos antes de `airspaceExtras` existir reidratam sem ele; e
    // chaves que desde então viraram tipos conhecidos devem ser podadas para não
    // duplicar o toggle (conhecido vive nos booleanos, desconhecido no bucket).
    const extras = parsed.airspaceExtras ?? {}
    const prunedExtras: Record<string, boolean> = {}
    for (const [type, active] of Object.entries(extras)) {
      if (!isKnownAirspaceType(type)) prunedExtras[type] = active
    }

    this._updateState({
      ...INITIAL_STATE,
      ...parsed,
      airspaceExtras: prunedExtras
    })
  }

  setNightMode(nightMode: boolean) {
    this._updateStateProp('nightMode', nightMode)
  }

  setLayer(layer: keyof MapLayerStateProps, value: boolean) {
    this._updateStateProp(layer, value)
  }

  // Base map
  setBaseMap(baseMap: BaseMapType) {
    this._updateStateProp('baseMap', baseMap)
  }

  // Charts
  setBaseChart(baseChart: BaseChartType) {
    this._updateStateProp('baseChart', baseChart)
  }

  setOverlayChart(overlayChart: OverlayChartType) {
    this._updateStateProp('overlayChart', overlayChart)
  }

  setOverlay2Chart(overlay2Chart: Overlay2ChartType) {
    this._updateStateProp('overlay2Chart', overlay2Chart)
  }

  setOverlay3Chart(overlay3Chart: Overlay3ChartType) {
    this._updateStateProp('overlay3Chart', overlay3Chart)
  }

  // Airspace
  setAdiz(adiz: boolean) {
    this._updateStateProp('adiz', adiz)
  }

  setAfis(afis: boolean) {
    this._updateStateProp('afis', afis)
  }

  setAtz(atz: boolean) {
    this._updateStateProp('atz', atz)
  }

  setCtr(ctr: boolean) {
    this._updateStateProp('ctr', ctr)
  }

  setFreeRouteAreas(freeRouteAreas: boolean) {
    this._updateStateProp('freeRouteAreas', freeRouteAreas)
  }

  setFis(fis: boolean) {
    this._updateStateProp('fis', fis)
  }

  setFir(fir: boolean) {
    this._updateStateProp('fir', fir)
  }

  setFirSector(firSector: boolean) {
    this._updateStateProp('firSector', firSector)
  }

  setFiz(fiz: boolean) {
    this._updateStateProp('fiz', fiz)
  }

  setFca(fca: boolean) {
    this._updateStateProp('fca', fca)
  }

  setFcaReh(fcaReh: boolean) {
    this._updateStateProp('fcaReh', fcaReh)
  }

  setFcaAd(fcaAd: boolean) {
    this._updateStateProp('fcaAd', fcaAd)
  }

  setSuaDangerous(suaDangerous: boolean) {
    this._updateStateProp('suaDangerous', suaDangerous)
  }

  setSuaProhibited(suaProhibited: boolean) {
    this._updateStateProp('suaProhibited', suaProhibited)
  }

  setSuaRestricted(suaRestricted: boolean) {
    this._updateStateProp('suaRestricted', suaRestricted)
  }

  setTma(tma: boolean) {
    this._updateStateProp('tma', tma)
  }

  setTmaSector(tmaSector: boolean) {
    this._updateStateProp('tmaSector', tmaSector)
  }

  setCta(cta: boolean) {
    this._updateStateProp('cta', cta)
  }

  setCtaSector(ctaSector: boolean) {
    this._updateStateProp('ctaSector', ctaSector)
  }

  // Liga/desliga a camada de um tipo de airspace escondendo o split
  // conhecido-vs-desconhecido: tipos conhecidos vivem como booleanos nomeados;
  // desconhecidos (cruas do dataset) vivem no bucket `airspaceExtras`.
  isAirspaceLayerActive(type: string): boolean {
    const snapshot = this.getStateSnapshot()
    if (isKnownAirspaceType(type)) return !!snapshot[type]
    return snapshot.airspaceExtras?.[type] ?? false
  }

  setAirspaceLayer(type: string, value: boolean) {
    if (isKnownAirspaceType(type)) {
      this._updateStateProp(type, value)
      return
    }
    const extras = this.getStateSnapshot().airspaceExtras ?? {}
    this._updateStateProp('airspaceExtras', { ...extras, [type]: value })
  }

  setSeveralAirspaces(airspaces: Map<AirspaceType, boolean>) {
    const state = this.getStateSnapshot()
    const newState = { ...state }
    airspaces.forEach((value, key) => {
      newState[key] = value
    })

    this._updateState(newState)
  }

  isChartActive(): boolean {
    const snapshot = this.getStateSnapshot()
    return !!(snapshot.baseMap !== 'ROAD' || snapshot.baseChart || snapshot.overlayChart || snapshot.overlay2Chart)
  }

  isAirspaceActive(): boolean {
    const snapshot = this.getStateSnapshot()
    return (
      snapshot.adiz ||
      snapshot.afis ||
      snapshot.atz ||
      snapshot.cta ||
      snapshot.ctaSector ||
      snapshot.ctr ||
      snapshot.freeRouteAreas ||
      snapshot.fis ||
      snapshot.fir ||
      snapshot.firSector ||
      snapshot.fiz ||
      snapshot.fca ||
      snapshot.fcaReh ||
      snapshot.fcaAd ||
      snapshot.suaDangerous ||
      snapshot.suaProhibited ||
      snapshot.suaRestricted ||
      snapshot.tma ||
      snapshot.tmaSector ||
      Object.values(snapshot.airspaceExtras ?? {}).some(Boolean)
    )
  }

  setAerodromes(aerodromes: boolean) {
    this._updateStateProp('aerodromes', aerodromes)
  }

  setHelipads(helipads: boolean) {
    this._updateStateProp('helipads', helipads)
  }

  setPavedRunway(pavedRunway: boolean) {
    this._updateStateProp('pavedRunway', pavedRunway)
  }

  setNightOperation(nightOperation: boolean) {
    this._updateStateProp('nightOperation', nightOperation)
  }

  setAts(ats: boolean) {
    this._updateStateProp('ats', ats)
  }

  setIfr(ifr: boolean) {
    this._updateStateProp('ifr', ifr)
  }

  setMinRunwayLength(minRunwayLength: number) {
    this._updateStateProp('minRunwayLength', minRunwayLength)
  }

  setUserWaypoints(userWaypoints: boolean) {
    this._updateStateProp('userWaypoints', userWaypoints)
  }

  setObstacles(obstacles: boolean) {
    this._updateStateProp('obstacles', obstacles)
  }

  setNavaids(navaids: boolean) {
    this._updateStateProp('navaids', navaids)
  }

  getAllADHP(): Partial<MapLayerStateProps> {
    const snapshot = this.getStateSnapshot()
    return {
      aerodromes: snapshot.aerodromes,
      helipads: snapshot.helipads,
      pavedRunway: snapshot.pavedRunway,
      nightOperation: snapshot.nightOperation,
      ats: snapshot.ats,
      ifr: snapshot.ifr,
      minRunwayLength: snapshot.minRunwayLength,
      userWaypoints: snapshot.userWaypoints
    }
  }

  setAllADHP(allADHP: Partial<MapLayerStateProps>) {
    const snapshot = this.getStateSnapshot()
    this.setAerodromes(allADHP.aerodromes ?? snapshot.aerodromes)
    this.setHelipads(allADHP.helipads ?? snapshot.helipads)
    this.setPavedRunway(allADHP.pavedRunway ?? snapshot.pavedRunway)
    this.setNightOperation(allADHP.nightOperation ?? snapshot.nightOperation)
    this.setAts(allADHP.ats ?? snapshot.ats)
    this.setIfr(allADHP.ifr ?? snapshot.ifr)
    this.setMinRunwayLength(allADHP.minRunwayLength ?? snapshot.minRunwayLength)
    this.setUserWaypoints(allADHP.userWaypoints ?? snapshot.userWaypoints)
  }

  isADHPActive(): boolean {
    const snapshot = this.getStateSnapshot()
    return snapshot.aerodromes || snapshot.helipads || snapshot.userWaypoints || snapshot.obstacles || snapshot.navaids
  }

  isADHPFilterActive(): boolean {
    const snapshot = this.getStateSnapshot()
    return (
      snapshot.pavedRunway || snapshot.nightOperation || snapshot.ats || snapshot.ifr || snapshot.minRunwayLength > 0
    )
  }

  // User Charts
  setUserCharts(userCharts: UserChartInfo[]) {
    this._updateStateProp('userCharts', userCharts)
  }

  addActiveUserChart(info: UserChartInfo) {
    const current = this.getStateSnapshot().userCharts
    if (current.some((chart) => chart.id === info.id)) return
    if (current.length >= MAX_ACTIVE_USER_CHARTS) return
    this._updateStateProp('userCharts', [...current, info])
  }

  removeActiveUserChart(id: string) {
    const current = this.getStateSnapshot().userCharts
    const next = current.filter((chart) => chart.id !== id)
    if (next.length === current.length) return
    this._updateStateProp('userCharts', next)
  }

  // User Geometries
  setUserGeometries(userGeometries: UserGeometryInfo[]) {
    this._updateStateProp('userGeometries', userGeometries)
  }

  addActiveUserGeometry(info: UserGeometryInfo) {
    const current = this.getStateSnapshot().userGeometries
    if (current.some((geometry) => geometry.id === info.id)) return
    if (current.length >= MAX_ACTIVE_USER_GEOMETRIES) return
    this._updateStateProp('userGeometries', [...current, info])
  }

  removeActiveUserGeometry(id: string) {
    const current = this.getStateSnapshot().userGeometries
    const next = current.filter((geometry) => geometry.id !== id)
    if (next.length === current.length) return
    this._updateStateProp('userGeometries', next)
  }

  // Reconciles the cached display name of an active geometry with a rename in the
  // repository. The cache is seeded at toggle-time and is persisted, so without
  // this the map/InfoPanel title stays stale after a rename. No-op when the
  // geometry isn't active or the name is unchanged (keeps callers from looping).
  renameActiveUserGeometry(id: string, name: string) {
    const current = this.getStateSnapshot().userGeometries
    const index = current.findIndex((geometry) => geometry.id === id)
    if (index === -1 || current[index].name === name) return
    const next = [...current]
    next[index] = { ...next[index], name }
    this._updateStateProp('userGeometries', next)
  }

  getKey(): string {
    return 'mapLayer'
  }
}
